'use client'

import { useState } from 'react'
import type { ComponentProps } from 'react'
import { clsx } from 'clsx'
import { useCombobox } from 'downshift'
import { t } from '@/lang'
import { describedBy, Field } from '@/ui/Field'
import { CrossIcon } from '@/ui/icons/CrossIcon'
import { Input } from '@/ui/Input'

export type ComboboxItem = {
  value: string
  label: string
}

const noMatch = t('combobox.no-matches')

type Props = {
  /** Id of the text input, so a `<label for>` and the validation message can point at it. */
  id: string
  label: string
  items: readonly ComboboxItem[]
  /** `value` of the chosen item, empty when nothing is chosen. Typed text is not a value. */
  value: string
  onChange: (value: string) => void
  onBlur?: ComponentProps<'input'>['onBlur']
  inputRef?: ComponentProps<'input'>['ref']
  error?: string
  required?: boolean
  disabled?: boolean
  placeholder?: string
  className?: string
}

export function Combobox({
  id,
  label,
  items,
  value,
  onChange,
  onBlur,
  inputRef,
  error,
  required,
  disabled,
  placeholder,
  className,
}: Props) {
  const [query, setQuery] = useState('')
  const selectedItem = items.find((item) => item.value === value) ?? null

  const needle = query.trim().toLowerCase()
  const matches =
    needle && needle !== selectedItem?.label.toLowerCase()
      ? items.filter((item) => item.label.toLowerCase().includes(needle))
      : [...items]

  const { isOpen, highlightedIndex, getLabelProps, getInputProps, getMenuProps, getItemProps, selectItem } =
    useCombobox({
      inputId: id,
      items: matches,
      selectedItem,
      itemToString: (item) => item?.label ?? '',
      onInputValueChange: ({ inputValue }) => setQuery(inputValue),
      onSelectedItemChange: ({ selectedItem: chosen }) => onChange(chosen?.value ?? ''),
      // Leaving half-typed text in the field would show a value the form does not hold.
      stateReducer: (state, { type, changes }) =>
        type === useCombobox.stateChangeTypes.InputBlur
          ? { ...changes, inputValue: (changes.selectedItem ?? state.selectedItem)?.label ?? '' }
          : changes,
    })

  return (
    <Field fieldId={id} error={error} reserveErrorSpace className={className}>
      <div className="flex items-baseline">
        <label {...getLabelProps()} className="text-label text-neutral-800">
          {label}
        </label>
        {required ? (
          <span aria-hidden="true" className="ms-1 text-label text-error-600">
            *
          </span>
        ) : null}
      </div>
      <div className="relative">
        <Input
          trailing={
            selectedItem && !disabled ? (
              <button
                type="button"
                aria-label={t('combobox.clear', { label })}
                className="flex size-6 items-center justify-center rounded-full text-neutral-700 hover:text-neutral-800 focus-visible:focus-ring"
                onClick={() => {
                  selectItem(null)
                  // The button disappears with the value; focus goes back to the field instead of the page.
                  document.getElementById(id)?.focus()
                }}
              >
                <CrossIcon className="w-3" />
              </button>
            ) : null
          }
          {...getInputProps({
            disabled,
            placeholder,
            onBlur,
            ref: inputRef,
            'aria-describedby': describedBy(id, error),
            'aria-invalid': error ? true : undefined,
            'aria-required': required ? true : undefined,
          })}
        />
        <ul
          {...getMenuProps()}
          className={clsx(
            'absolute inset-x-0 z-10 mt-1 max-h-60 overflow-y-auto rounded-sm border border-neutral-400 bg-white',
            !isOpen && 'hidden'
          )}
        >
          {isOpen
            ? matches.map((item, index) => (
                <li
                  key={item.value}
                  {...getItemProps({ item, index })}
                  className={clsx(
                    'cursor-pointer px-2.5 py-2 text-field text-neutral-800',
                    index === highlightedIndex && 'bg-complementary-200'
                  )}
                >
                  {item.label}
                </li>
              ))
            : null}
          {isOpen && matches.length === 0 ? (
            <li role="presentation" className="px-2.5 py-2 text-field text-neutral-700">
              {noMatch}
            </li>
          ) : null}
        </ul>
      </div>
      <output className="sr-only">{isOpen && matches.length === 0 ? noMatch : ''}</output>
    </Field>
  )
}
